import React from 'react';
import { ChevronRight } from 'lucide-react';
import { ModuleArt, type ModuleArtKey } from './moduleArt/ModuleIllustrations';

export interface ModuleHubStat {
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}

export interface ModuleHubCardProps {
  moduleKey: ModuleArtKey;
  title: string;
  description: string;
  stats?: ModuleHubStat[];
  onClick: () => void;
}

const toneClasses: Record<NonNullable<ModuleHubStat['tone']>, string> = {
  default: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  success: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300',
  warning: 'bg-amber-50 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
  danger: 'bg-rose-50 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300',
};

export function ModuleHubCard({
  moduleKey,
  title,
  description,
  stats = [],
  onClick,
}: ModuleHubCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative w-full min-w-0 overflow-hidden rounded-xl border border-zinc-200/90 bg-white p-4 text-left shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600 dark:focus-visible:ring-offset-zinc-950 sm:p-5"
    >
      <div className="flex items-start gap-3.5">
        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-xl shadow-sm ring-1 ring-zinc-200/80 transition-transform duration-200 group-hover:scale-105 dark:ring-zinc-600/80 sm:h-12 sm:w-12">
          <ModuleArt moduleKey={moduleKey} className="h-full w-full" />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              {title}
            </h3>
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-zinc-300 transition-transform group-hover:translate-x-0.5 group-hover:text-emerald-600 dark:text-zinc-600 dark:group-hover:text-emerald-400" />
          </div>
          <p className="mt-1 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
            {description}
          </p>
        </div>
      </div>

      {stats.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-100 pt-3.5 dark:border-zinc-800">
          {stats.map((stat) => (
            <span
              key={stat.label}
              className={`inline-flex max-w-full items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium ${toneClasses[stat.tone ?? 'default']}`}
            >
              <span className="truncate opacity-75">{stat.label}</span>
              <span className="truncate font-semibold tabular-nums">{stat.value}</span>
            </span>
          ))}
        </div>
      ) : null}
    </button>
  );
}
